import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, ApiError } from '~/lib/api-client';
import { chatKeys, chatTaskKeys } from '~/lib/query-keys';
import type { ChatTask, ChatTaskProgress } from '~/features/chat/chat-tasks-view';
import { isSettledTaskStatus, listsMissingTask, newlyDeliveredTaskIds } from '~/features/chat/chat-tasks-view';

/**
 * FRONT-3211: the chat's tasks, loaded on open and on every reload (the
 * strip rebuilds from the server) and kept live by the `chat-task-changed`
 * and `chat-task-progress` events (use-sse.ts). With `eddy.chat-jobs` off
 * the route answers 404, which here means "this chat has no tasks" — never an
 * error. Any OTHER failure stays an error, so the chip row can say so instead
 * of looking like a chat with nothing running.
 */

/** Backstop poll while something is out: events are the fast path, this catches a missed one. */
const LIVE_POLL_MS = 15_000;
/** FRONT-3263: how often, and how many times, a "moved to job" card re-reads a list that lacks its job. */
const MISSING_JOB_RETRY_MS = 3_000;
const MISSING_JOB_TRIES = 4;

function tasksPath(sessionId: string): string {
  return `/api/chat/${encodeURIComponent(sessionId)}/tasks`;
}

export function isFeatureOffError(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}

export async function fetchChatTasks(sessionId: string): Promise<ChatTask[]> {
  try {
    return (await api.get<{ tasks: ChatTask[] }>(tasksPath(sessionId))).tasks ?? [];
  } catch (err) {
    if (isFeatureOffError(err)) return [];
    throw err;
  }
}

/**
 * FRONT-3263: a re-read that finds a delivery whose event never reached this
 * tab re-reads the chat's history FIRST, then lets the list move — the same
 * order `use-sse.ts` keeps for the delivered event, so a held send never goes
 * out above the answer.
 */
async function fetchChatTasksFor(queryClient: QueryClient, sessionId: string): Promise<ChatTask[]> {
  const next = await fetchChatTasks(sessionId);
  const prev = queryClient.getQueryData<ChatTask[]>(chatTaskKeys.list(sessionId));
  if (newlyDeliveredTaskIds(prev, next).length > 0) {
    await queryClient.refetchQueries({ queryKey: [...chatKeys.all, 'history', sessionId] }).catch(() => {
      // A failed history re-read still lets the list move; the chip must not hang on it.
    });
  }
  return next;
}

export function useChatTasks(sessionId: string | null | undefined, enabled = true) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: chatTaskKeys.list(sessionId ?? ''),
    queryFn: () => fetchChatTasksFor(queryClient, sessionId!),
    enabled: enabled && !!sessionId,
    staleTime: 5_000,
    refetchInterval: (q) => {
      const tasks = q.state.data as ChatTask[] | undefined;
      return tasks?.some((t) => !t.deliveredAt && t.status !== 'cancelled') ? LIVE_POLL_MS : false;
    },
  });
}

/** Jobs whose re-read is running now: a second card for the same job asks nothing and leaves it to the first. */
const askingForJob = new Set<string>();

/**
 * FRONT-3263: a "moved to job" card this page watched hand off makes sure the chip row knows its job,
 * with no reliance on the job's start event: every loaded job list that lacks
 * it is re-read, a few times, a few seconds apart, until it shows up (one run per job at a time). The
 * job's row exists before the card does (the hand-off writes it first), so
 * the first re-read normally finds it; the retries cover a slow replica.
 * Once known, the list's own backstop poll carries the job to its end.
 */
export async function ensureChatTaskListed(queryClient: QueryClient, taskId: string): Promise<void> {
  if (askingForJob.has(taskId)) return;
  askingForJob.add(taskId);
  try {
    for (let attempt = 0; attempt < MISSING_JOB_TRIES; attempt++) {
      const missing = listsMissingTask(queryClient.getQueriesData<ChatTask[]>({ queryKey: [...chatTaskKeys.all, 'list'], type: 'active' }), taskId);
      if (missing.length === 0) return;
      if (attempt > 0) await new Promise((r) => setTimeout(r, MISSING_JOB_RETRY_MS));
      // Never cancel a read already on its way: it may be the one that finds the job.
      await Promise.all(missing.map((queryKey) => queryClient.refetchQueries({ queryKey, exact: true }, { cancelRefetch: false }))).catch(() => {
        // A failed re-read is retried; the list's own error state shows a lasting failure.
      });
    }
  } finally {
    askingForJob.delete(taskId);
  }
}

/** The card's side of {@link ensureChatTaskListed}; null asks nothing. */
export function useEnsureChatTaskListed(taskId: string | null): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (taskId) void ensureChatTaskListed(queryClient, taskId);
  }, [queryClient, taskId]);
}

/** Live steps per agent row; filled only by events (the server never stores them). */
export function useChatTaskProgress(sessionId: string | null | undefined) {
  return useQuery<ChatTaskProgress>({
    queryKey: chatTaskKeys.progress(sessionId ?? ''),
    queryFn: () => ({}),
    enabled: false,
    initialData: {},
    staleTime: Infinity,
  });
}

/**
 * The panel's "Stop task": stops only that task. The chip leaves at once
 * (like the mockup and the voice strip's Stop); a refused stop puts it back
 * and says so.
 */
export function useCancelChatTask(sessionId: string | null | undefined) {
  const queryClient = useQueryClient();
  const key = chatTaskKeys.list(sessionId ?? '');
  return useMutation({
    mutationFn: (taskId: string) =>
      api.post<{ ok: boolean }>(`${tasksPath(sessionId!)}/${encodeURIComponent(taskId)}/cancel`, {}),
    onMutate: async (taskId) => {
      await queryClient.cancelQueries({ queryKey: key });
      const before = queryClient.getQueryData<ChatTask[]>(key);
      queryClient.setQueryData<ChatTask[]>(key, (tasks) =>
        tasks?.map((t) => (t.id === taskId && !isSettledTaskStatus(t.status) ? { ...t, status: 'cancelled' } : t)),
      );
      return { before };
    },
    onError: (_err, _taskId, ctx) => {
      if (ctx?.before) queryClient.setQueryData(key, ctx.before);
      toast.error("Couldn't stop that job. It may have finished already.");
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: key }),
  });
}

/**
 * The composer's Stop during a result turn: the reply on screen IS the
 * result turn, which the server started, so it is stopped through the
 * server's queue (the route stops only a RUNNING result turn; one still
 * waiting behind the person's own reply is left alone). Best-effort: the
 * composer's own Stop (the turn's background run) goes out alongside it.
 */
export function useStopTaskAnswer(sessionId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ ok: boolean; stopped: boolean }>(`${tasksPath(sessionId!)}/stop-answer`, {}),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: chatTaskKeys.list(sessionId ?? '') }),
  });
}
